// src/services/api.ts
// HTTP client for all backend API endpoints with credentials and error handling

import type { User, Household, Member, Invite, ShoppingList, ListItem, KnownItem, UserRole } from '../types';

const API_BASE = '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && !(options.body instanceof FormData) && !(options.body instanceof ArrayBuffer)) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(API_BASE + path, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    let errorMsg = `HTTP Error ${response.status}`;
    try {
      const data = await response.json();
      if (data.error) errorMsg = data.error;
    } catch {}
    throw new Error(errorMsg);
  }

  return response.json();
}

export const api = {
  // Auth
  async loginWithGoogle(credential: string): Promise<{ ok: boolean; user: User; currentHousehold: Household; households: Household[] }> {
    return request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  },

  async loginDemo(demoUser: { email: string; name: string; picture?: string }): Promise<{ ok: boolean; user: User; currentHousehold: Household; households: Household[] }> {
    return request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ demoUser }),
    });
  },

  async checkSession(): Promise<{ authenticated: boolean; user: User | null; households: Household[] }> {
    return request('/auth/session');
  },

  async logout(): Promise<{ ok: boolean }> {
    return request('/auth/logout', { method: 'POST' });
  },

  // Households
  async getHouseholds(): Promise<{ households: Household[] }> {
    return request('/households');
  },

  async createHousehold(name: string): Promise<{ ok: boolean; household: Household }> {
    return request('/households', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  async getHousehold(id: string): Promise<{ household: Household }> {
    return request(`/households/${id}`);
  },

  async updateHousehold(id: string, name: string): Promise<{ ok: boolean; name: string }> {
    return request(`/households/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  },

  async deleteHousehold(id: string): Promise<{ ok: boolean }> {
    return request(`/households/${id}`, { method: 'DELETE' });
  },

  // Members
  async getMembers(householdId: string): Promise<{ members: Member[] }> {
    return request(`/households/${householdId}/members`);
  },

  async updateMemberRole(householdId: string, targetUserId: string, newRole: UserRole): Promise<{ ok: boolean }> {
    return request(`/households/${householdId}/members`, {
      method: 'PATCH',
      body: JSON.stringify({ targetUserId, newRole }),
    });
  },

  async removeMember(householdId: string, targetUserId?: string): Promise<{ ok: boolean }> {
    const query = targetUserId ? `?userId=${encodeURIComponent(targetUserId)}` : '';
    return request(`/households/${householdId}/members${query}`, { method: 'DELETE' });
  },

  // Invites
  async getInvites(householdId: string): Promise<{ invites: Invite[] }> {
    return request(`/households/${householdId}/invites`);
  },

  async createInvite(householdId: string, role: 'editor' | 'shopper', expiresInDays: number = 7, maxUses: number = 0): Promise<{ ok: boolean; invite: Invite }> {
    return request(`/households/${householdId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ role, expiresInDays, maxUses }),
    });
  },

  async revokeInvite(householdId: string, inviteId: string): Promise<{ ok: boolean }> {
    return request(`/households/${householdId}/invites?inviteId=${encodeURIComponent(inviteId)}`, {
      method: 'DELETE',
    });
  },

  async getInviteInfo(code: string): Promise<{ valid: boolean; code: string; householdId: string; householdName: string; role: 'editor' | 'shopper'; inviterName: string; expiresAt: string | null }> {
    return request(`/invites/info?code=${encodeURIComponent(code)}`);
  },

  async joinHousehold(code: string): Promise<{ ok: boolean; joined?: boolean; alreadyMember?: boolean; household: Household }> {
    return request('/invites/join', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  // Lists
  async getLists(householdId: string): Promise<{ lists: ShoppingList[]; role: UserRole }> {
    return request(`/lists?householdId=${encodeURIComponent(householdId)}`);
  },

  async createList(householdId: string, name: string): Promise<{ ok: boolean; list: ShoppingList }> {
    return request('/lists', {
      method: 'POST',
      body: JSON.stringify({ householdId, name }),
    });
  },

  async getList(listId: string): Promise<{ list: ShoppingList; role: UserRole }> {
    return request(`/lists/${listId}`);
  },

  async updateList(listId: string, updates: Partial<{ name: string; archived: boolean; shopDone: boolean; readyToShop: boolean }>): Promise<{ ok: boolean }> {
    return request(`/lists/${listId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  async finalizeList(listId: string): Promise<{ ok: boolean; listId: string; readyToShop: boolean; message: string }> {
    return request(`/lists/${listId}/finalize`, {
      method: 'POST',
    });
  },

  async deleteList(listId: string): Promise<{ ok: boolean }> {
    return request(`/lists/${listId}`, { method: 'DELETE' });
  },

  // Items
  async addItem(listId: string, name: string, qty: string = '1', aisle: string = 'General'): Promise<{ ok: boolean; item: ListItem }> {
    return request(`/lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify({ name, qty, aisle }),
    });
  },

  async updateItems(listId: string, items: Array<{ id: string; checked?: boolean; skipped?: boolean }>): Promise<{ ok: boolean }> {
    return request(`/lists/${listId}/items`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  },

  async deleteItem(listId: string, itemId: string): Promise<{ ok: boolean }> {
    return request(`/lists/${listId}/items?itemId=${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
    });
  },

  async finishShop(listId: string, uncollectedItemIds: string[]): Promise<{ ok: boolean; skippedCount: number }> {
    return request(`/lists/${listId}/finish`, {
      method: 'POST',
      body: JSON.stringify({ uncollectedItemIds }),
    });
  },

  // Suggestions
  async getSuggestions(householdId: string): Promise<{ items: KnownItem[] }> {
    return request(`/suggestions?householdId=${encodeURIComponent(householdId)}`);
  },

  async learnSuggestion(householdId: string, name: string, aisle: string): Promise<{ ok: boolean }> {
    return request('/suggestions', {
      method: 'POST',
      body: JSON.stringify({ householdId, name, aisle }),
    });
  },

  // Meal Plan Images
  async uploadMealPlan(listId: string, imageBlob: Blob): Promise<{ ok: boolean; url: string; updatedAt: number }> {
    return request(`/images/${listId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'image/jpeg' },
      body: imageBlob,
    });
  },

  async deleteMealPlan(listId: string): Promise<{ ok: boolean }> {
    return request(`/images/${listId}`, { method: 'DELETE' });
  },
};
